import { Component, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { NgIcon } from '@ng-icons/core';
import { isPlatformBrowser } from '@angular/common';
import { FirebaseMessagingService } from './core/services/notifications/firebase-messaging.service';
import { SocketEvents, SocketIoService } from './core/services/web-socket/socket-io.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InboxService } from './core/services/inbox/inbox.service';
import { Conversation } from './shared/models/inbox.model';
import { UserService, UserType } from './core/services/user-management/user.service';
import { ProfessionalScreenerService } from './core/services/self-takers/professional-screener.service';
import { ScreenerCenterService } from './core/services/self-takers/screener-center.service';
import { HealthSecretaryService } from './core/services/self-takers/health-secretary.service';
import { ClinicalLaboratoryService } from './core/services/self-takers/clinical-laboratory.service';
import { ScreeningCycle } from './shared/models/self-takers/self-taker.model';
import { SelfTakerSurveyService } from './core/services/self-takers/self-taker-survey.service';
import { AuthService } from './core/services/user-management/auth.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { DoctorService } from './core/services/treatments/doctor.service';
import { NurseService } from './core/services/treatments/nurse.service';
import { IpsService } from './core/services/treatments/ips.service';
import { PharmaceuticalService } from './core/services/treatments/pharmaceutical.service';
import { InsurerService } from './core/services/treatments/insurer.service';
import { Treatment } from './shared/models/treatments/treatment.model';
import { Effect } from './shared/models/treatments/effects.model';
import { DashboardInteractionItem } from './shared/models/treatments/dashboardInteraction.model';
import { DoctorUserInfo, NurseUserInfo } from './shared/models/users/user.model';
import { Professional } from './shared/models/users/professional.model';
import { HighDemandMolecule } from './shared/models/treatments/high-demand-molecule.model';
import { Latency } from './shared/models/treatments/latency.model';
import { DispenserService } from './core/services/treatments/dispenser.service';
import { PointsService, SourceType } from './core/services/points/points.service';
import { PointsWallet, PointsProfile, PointsHistoryItem } from './shared/models/points/points.model';
import { SelfTakerSurvey } from './shared/models/self-takers/self-taker-survey.model';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastModule, NgIcon, ConfirmDialogModule, ButtonModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly title = signal('drazamed-web');
  private platformId = inject(PLATFORM_ID);

  private readonly firebaseMessagingService = inject(FirebaseMessagingService);
  private readonly socketService = inject(SocketIoService);
  private readonly inboxService = inject(InboxService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);

  /* Screening Cycles Services */
  private readonly professionalScreenerService = inject(ProfessionalScreenerService);
  private readonly screenerCenterService = inject(ScreenerCenterService);
  private readonly healthSecretaryService = inject(HealthSecretaryService);
  private readonly clinicalLaboratoryService = inject(ClinicalLaboratoryService);
  private readonly selfTakerSurveyService = inject(SelfTakerSurveyService);

  /* Treatment Services */
  private readonly doctorService = inject(DoctorService);
  private readonly nurseService = inject(NurseService);
  private readonly ipsService = inject(IpsService);
  private readonly pharmaceuticalService = inject(PharmaceuticalService);
  private readonly insurerService = inject(InsurerService);
  private readonly dispenserService = inject(DispenserService);
  private readonly pointsService = inject(PointsService);

  constructor() {
    this.initializeApp();
    this.initializeSockets();
  }

  initializeApp(): void {
    this.authService.initializePublicKey();
    this.firebaseMessagingService.initializeFirebaseMessaging();
  }


  initializeSockets(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.socketService.listen(SocketEvents.NEW_CONVERSATION)
      .pipe(takeUntilDestroyed())
      .subscribe((conversation) => {
        this.inboxService.addNewConversation(conversation.data as Conversation);
      });

    this.socketService.listen(SocketEvents.REMOVE_CONVERSATION)
      .pipe(takeUntilDestroyed())
      .subscribe((conversation) => {
        this.inboxService.removeConversation(conversation.data as Conversation);
      });

    const handleScreeningCycleUpdate = (data: any) => {
      if (this.userService.userData()?.user_type === UserType.PROFESSIONAL_SCREENER) {
        this.professionalScreenerService.manageProfessionalScreenerData(data.data.screening_cycle as ScreeningCycle);
      }
      if (this.userService.userData()?.user_type === UserType.SCREENER_CENTER) {
        this.screenerCenterService.manageScreenerCenterData(data.data.screening_cycle as ScreeningCycle);
      }
      if (this.userService.userData()?.user_type === UserType.HEALTH_SECRETARY) {
        this.healthSecretaryService.manageHealthSecretaryData(data.data.screening_cycle as ScreeningCycle);
      }
      if (this.userService.userData()?.user_type === UserType.CLINICAL_LABORATORY) {
        // console.log('data.data.screening_cycle', data.data.screening_cycle);
        this.clinicalLaboratoryService.manageClinicalLaboratoryData(data.data.screening_cycle as ScreeningCycle);
      }
    };

    this.socketService.listen(SocketEvents.NEW_SCREENING_CYCLE)
      .pipe(takeUntilDestroyed())
      .subscribe(handleScreeningCycleUpdate);

    this.socketService.listen(SocketEvents.SCREENING_CYCLE_UPDATED)
      .pipe(takeUntilDestroyed())
      .subscribe(handleScreeningCycleUpdate);


    const handleTreatmentUpdate = (data: any) => {
      const treatment = data.data.treatment as Treatment;
      const socketType = data.socket_type as SocketEvents;
      const userType = this.userService.userData()?.user_type;

      console.log('data treatment', { treatment: treatment, socket_type: socketType });

      // console.log('data treatment', { treatment: treatment, socket_type: socketType });

      if (userType === UserType.DOCTOR) {
        this.doctorService.manageDoctorTreatment(treatment, socketType as 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT');
      }
      else if (userType === UserType.NURSE) {
        this.nurseService.manageNurseTreatment(treatment, socketType as 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT');
      }
      else if (userType === UserType.PHARMACEUTICAL) {
        this.pharmaceuticalService.managePharmaceuticalTreatment(treatment, socketType as 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT');
      }
      else if (userType === UserType.IPS) {
        this.ipsService.manageIpsTreatment(treatment, socketType as 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT');
      }
      else if (userType === UserType.INSURER) {
        this.insurerService.manageInsurerTreatment(treatment, socketType as 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT');
        this.insurerService.manageInsurerIpsTreatment(treatment, socketType as 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT');
        this.insurerService.manageInsurerPharmaceuticalTreatment(treatment, socketType as 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT');
      }
    };

    this.socketService.listen(SocketEvents.NEW_TREATMENT)
      .pipe(takeUntilDestroyed())
      .subscribe(handleTreatmentUpdate);

    this.socketService.listen(SocketEvents.REMOVE_TREATMENT)
      .pipe(takeUntilDestroyed())
      .subscribe(handleTreatmentUpdate);

    this.socketService.listen(SocketEvents.ALTER_TREATMENT)
      .pipe(takeUntilDestroyed())
      .subscribe(handleTreatmentUpdate);


    const handleAlertsUpdate = (data: any) => {
      const effects = data.data.effects as Effect[];
      const userType = this.userService.userData()?.user_type;

      // console.log('data effects', { effects: effects, user_type: userType });

      if (userType === UserType.DOCTOR) {
        this.doctorService.addEffectsToTreatments(effects);
      }
      else if (userType === UserType.NURSE) {
        this.nurseService.addEffectsToTreatments(effects);
      }
      else if (userType === UserType.PHARMACEUTICAL) {
        this.pharmaceuticalService.addEffectsToTreatments(effects);
      }
      else if (userType === UserType.IPS) {
        this.ipsService.addEffectsToTreatments(effects);
      }
      else if (userType === UserType.INSURER) {
        this.insurerService.addEffectsToTreatments(effects);
        this.insurerService.addEffectsToInsurerIpsTreatments(effects);
        this.insurerService.addEffectsToInsurerPharmaceuticalTreatments(effects);
      }
    };

    this.socketService.listen(SocketEvents.NEW_ALERTS)
      .pipe(takeUntilDestroyed())
      .subscribe(handleAlertsUpdate);

    const handleProfDashboardInteractionUpdate = (data: any) => {
      const interaction = data.data as DashboardInteractionItem;
      const userType = this.userService.userData()?.user_type;

      if (userType === UserType.DOCTOR) {
        this.doctorService.updateDashboardInteraction(interaction);
      }
      else if (userType === UserType.NURSE) {
        this.nurseService.updateDashboardInteraction(interaction);
      }
      else if (userType === UserType.PHARMACEUTICAL) {
        this.pharmaceuticalService.updateDashboardInteraction(interaction);
      }
      else if (userType === UserType.IPS) {
        this.ipsService.updateDashboardInteraction(interaction);
      }
      else if (userType === UserType.INSURER) {
        this.insurerService.updateDashboardInteractionInIps(interaction);
        this.insurerService.updateDashboardInteractionInPharmaceuticals(interaction);
      }
    };

    this.socketService.listen(SocketEvents.PROF_DASHBOARD_INTERACTION)
      .pipe(takeUntilDestroyed())
      .subscribe(handleProfDashboardInteractionUpdate);


    const handleNewProfessional = (data: any) => {
      const professional = data.data as DoctorUserInfo | NurseUserInfo;
      const userType = this.userService.userData()?.user_type;

      if (userType === UserType.IPS) {
        this.ipsService.addProfessional(professional);
      }
      else if (userType === UserType.PHARMACEUTICAL) {
        this.pharmaceuticalService.addProfessional(professional);
      }
    };

    this.socketService.listen(SocketEvents.NEW_PROFESSIONAL)
      .pipe(takeUntilDestroyed())
      .subscribe(handleNewProfessional);


    const handleNewSelfTakerUser = (data: any) => {
      const professional = data.data as Professional;
      const userType = this.userService.userData()?.user_type;

      if (userType === UserType.SCREENER_CENTER) {
        this.screenerCenterService.addSelfTakerUser(professional);
      }
    };

    this.socketService.listen(SocketEvents.NEW_SELF_TAKER_USER)
      .pipe(takeUntilDestroyed())
      .subscribe(handleNewSelfTakerUser);

    this.socketService.listen(SocketEvents.NEW_SELF_TAKER_SURVEY)
      .pipe(takeUntilDestroyed())
      .subscribe((payload) => {
        if (this.userService.userData()?.user_type === UserType.SCREENER_CENTER) {
          const survey = (payload.data as { survey?: SelfTakerSurvey })?.survey;
          if (survey) {
            this.selfTakerSurveyService.addSurvey(survey);
          }
        }
      });

    const handleNewMolecule = (data: any) => {
      const molecule = data.data as HighDemandMolecule;
      const userType = this.userService.userData()?.user_type;

      if (userType === UserType.IPS) {
        this.ipsService.addOrUpdateMolecule(molecule);
      }
      else if (userType === UserType.PHARMACEUTICAL) {
        this.pharmaceuticalService.addOrUpdateMolecule(molecule);
      }
      else if (userType === UserType.INSURER) {
        this.insurerService.addOrUpdateMoleculeInIps(molecule);
        this.insurerService.addOrUpdateMoleculeInPharmaceuticals(molecule);
      }
    };

    this.socketService.listen(SocketEvents.NEW_MOLECULE)
      .pipe(takeUntilDestroyed())
      .subscribe(handleNewMolecule);

    const handleLatency = (data: any) => {
      const latency = data.data as Latency;
      const userType = this.userService.userData()?.user_type;

      if (userType === UserType.NURSE) {
        this.nurseService.addOrUpdateLatency(latency);
      }
      else if (userType === UserType.PHARMACEUTICAL) {
        this.pharmaceuticalService.addOrUpdateLatency(latency);
      }
      else if (userType === UserType.INSURER) {
        this.insurerService.addOrUpdateLatencyInPharmaceuticals(latency);
      }
      else if (userType === UserType.DISPENSER) {
        this.dispenserService.addOrUpdateLatency({latency: data.data.latency, treatment: data.data.treatment});
      }
    };

    this.socketService.listen(SocketEvents.LATENCY)
      .pipe(takeUntilDestroyed())
      .subscribe(handleLatency);

    
    this.socketService.listen(SocketEvents.POINTS_AWARDED)
      .pipe(takeUntilDestroyed())
      .subscribe((pointsAwarded) => {
        const rawData = pointsAwarded.data as {
          wallet: PointsWallet;
          profile: PointsProfile;
          history: PointsHistoryItem;
        };
        
        const data = {
          wallet: rawData.wallet,
          profile: rawData.profile,
          history: rawData.history
        };
        
        this.pointsService.updatePointsAwarded(data);
      });
  }

}
